import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../services/api';

const BillingAdminPanel = () => {
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedClient, setSelectedClient] = useState(null);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showThresholdsModal, setShowThresholdsModal] = useState(false);
  const [showMarkupModal, setShowMarkupModal] = useState(false);
  const [refreshingRate, setRefreshingRate] = useState(false);

  useEffect(() => {
    fetchData();
  }, [search, filter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        ...(search && { Search: search }),
        ...(filter === 'low' && { Filter_Low_Balance: true }),
        ...(filter === 'critical' && { Filter_Critical: true }),
        Limit: 100
      };

      const [clientsRes, statsRes, rateRes] = await Promise.all([
        api.get('/admin/billing/clients', { params }),
        api.get('/admin/billing/stats'),
        api.get('/admin/billing/exchange-rates', { params: { Limit: 1 } })
      ]);

      setClients(clientsRes.data.Clients);
      setStats(statsRes.data);
      setExchangeRate(rateRes.data.Current_Rate);
    } catch (error) {
      toast.error('Ошибка загрузки данных: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshRate = async () => {
    setRefreshingRate(true);
    try {
      const res = await api.post('/admin/billing/exchange-rates/refresh');
      setExchangeRate(res.data.Rate);
      toast.success(`Курс обновлён: 1 USD = ${parseFloat(res.data.Rate).toFixed(2)} ₽`);
    } catch (error) {
      toast.error('Ошибка обновления курса: ' + (error.response?.data?.error || error.message));
    } finally {
      setRefreshingRate(false);
    }
  };

  const openDepositModal = (client) => {
    setSelectedClient(client);
    setShowDepositModal(true);
  };

  const openThresholdsModal = (client) => {
    setSelectedClient(client);
    setShowThresholdsModal(true);
  };

  const openMarkupModal = (client) => {
    setSelectedClient(client);
    setShowMarkupModal(true);
  };

  const getBadgeVariant = (balance, critical) => {
    const bal = parseFloat(balance);
    const crit = parseFloat(critical);
    if (bal <= crit) return 'bg-red-100 text-red-800';
    if (bal <= crit * 4) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Управление балансами клиентов</h1>
        <Link
          to="/admin/analytics"
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
        >
          📊 Аналитика выручки
        </Link>
      </div>

      {/* Exchange Rate Widget */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-2xl">💱</span>
          <div>
            <p className="text-xs text-amber-700 font-medium uppercase tracking-wide">Текущий курс USD/RUB</p>
            <p className="text-xl font-bold text-amber-900">
              {exchangeRate ? `1 USD = ${parseFloat(exchangeRate).toFixed(2)} ₽` : 'Загрузка...'}
            </p>
            <p className="text-xs text-amber-600">Используется для расчёта стоимости цикла. Обновляется автоматически раз в сутки.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/analytics"
            className="text-xs px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200"
          >
            📈 История курса
          </Link>
          <button
            onClick={handleRefreshRate}
            disabled={refreshingRate}
            className="text-xs px-3 py-1.5 bg-amber-200 text-amber-800 rounded-lg hover:bg-amber-300 disabled:opacity-50"
          >
            {refreshingRate ? '⏳ Обновление...' : '🔄 Обновить сейчас'}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Всего клиентов</p>
            <p className="text-2xl font-bold">{stats.Total_Clients}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Общий баланс</p>
            <p className="text-2xl font-bold">{parseFloat(stats.Total_Balance).toFixed(2)} ₽</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Низкий баланс</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.Low_Balance_Clients}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Критический</p>
            <p className="text-2xl font-bold text-red-600">{stats.Critical_Balance_Clients}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="flex gap-4">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск по email или компании..."
            className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          >
            <option value="all">Все клиенты</option>
            <option value="low">Низкий баланс</option>
            <option value="critical">Критический баланс</option>
          </select>
        </div>
      </div>

      {/* Clients Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Компания</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Баланс</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Наценка</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Пороги</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Потрачено</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Посл. списание</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Действия</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {clients.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    Клиенты не найдены
                  </td>
                </tr>
              ) : (
                clients.map((client) => (
                  <tr key={client.User_ID} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="font-medium text-gray-900 text-sm">{client.Company_Name || '—'}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {client.Email}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getBadgeVariant(client.Balance, client.Critical_Threshold)}`}>
                        {parseFloat(client.Balance).toFixed(2)} {client.Currency}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-semibold text-indigo-700">
                          +{parseFloat(client.Markup_Percent || 0).toFixed(1)}%
                        </span>
                        <button
                          onClick={() => openMarkupModal(client)}
                          className="text-gray-400 hover:text-indigo-600 text-xs"
                          title="Изменить наценку"
                        >
                          ✏️
                        </button>
                      </div>
                      {exchangeRate && parseFloat(client.Markup_Percent || 0) > 0 && (
                        <div className="text-xs text-gray-400 mt-0.5">
                          ×{(parseFloat(exchangeRate) * (1 + parseFloat(client.Markup_Percent || 0) / 100)).toFixed(2)} ₽/USD
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                      <div>⚠️ {client.Warning_Threshold} ₽</div>
                      <div>🔴 {client.Critical_Threshold} ₽</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {parseFloat(client.Total_Spent || 0).toFixed(2)} ₽
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                      {client.Last_Charge_At
                        ? new Date(client.Last_Charge_At).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' })
                        : '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <button onClick={() => openDepositModal(client)} className="text-indigo-600 hover:text-indigo-900" title="Пополнить баланс">💰</button>
                        <button onClick={() => openMarkupModal(client)} className="text-purple-600 hover:text-purple-900" title="Наценка">�</button>
                        <button onClick={() => openThresholdsModal(client)} className="text-gray-600 hover:text-gray-900" title="Настроить пороги">⚙️</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Deposit Modal */}
      {showDepositModal && selectedClient && (
        <DepositModal
          client={selectedClient}
          onClose={() => {
            setShowDepositModal(false);
            setSelectedClient(null);
          }}
          onSuccess={() => {
            fetchData();
            setShowDepositModal(false);
            setSelectedClient(null);
          }}
        />
      )}

      {/* Thresholds Modal */}
      {showThresholdsModal && selectedClient && (
        <ThresholdsModal
          client={selectedClient}
          onClose={() => { setShowThresholdsModal(false); setSelectedClient(null); }}
          onSuccess={() => { fetchData(); setShowThresholdsModal(false); setSelectedClient(null); }}
        />
      )}

      {/* Markup Modal */}
      {showMarkupModal && selectedClient && (
        <MarkupModal
          client={selectedClient}
          onClose={() => { setShowMarkupModal(false); setSelectedClient(null); }}
          onSuccess={() => { fetchData(); setShowMarkupModal(false); setSelectedClient(null); }}
        />
      )}
    </div>
  );
};

// Deposit Modal Component
const DepositModal = ({ client, onClose, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Введите корректную сумму');
      return;
    }

    setLoading(true);
    try {
      await api.post('/admin/billing/deposit', {
        User_ID: client.User_ID,
        Amount: parseFloat(amount),
        Comment: comment
      });
      toast.success('Баланс успешно пополнен');
      onSuccess();
    } catch (error) {
      toast.error('Ошибка пополнения: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const newBalance = amount ? (parseFloat(client.Balance) + parseFloat(amount)).toFixed(2) : client.Balance;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-medium mb-4">Пополнить баланс: {client.Company_Name || client.Email}</h3>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Текущий баланс
            </label>
            <p className="text-2xl font-bold">{parseFloat(client.Balance).toFixed(2)} {client.Currency}</p>
          </div>

          <div className="mb-4">
            <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
              Сумма пополнения
            </label>
            <input
              id="amount"
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Введите сумму..."
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              required
            />
          </div>

          <div className="mb-4">
            <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-1">
              Комментарий (опционально)
            </label>
            <textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Причина пополнения..."
              rows={3}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          {amount && (
            <div className="mb-4 p-3 bg-blue-50 rounded-md">
              <p className="text-sm text-blue-800">
                Новый баланс: <strong>{newBalance} {client.Currency}</strong>
              </p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Пополнение...' : 'Пополнить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Thresholds Modal Component
const ThresholdsModal = ({ client, onClose, onSuccess }) => {
  const [critical, setCritical] = useState(client.Critical_Threshold);
  const [warning, setWarning] = useState(client.Warning_Threshold);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    try {
      await api.put('/admin/billing/thresholds', {
        User_ID: client.User_ID,
        Critical_Threshold: parseFloat(critical),
        Warning_Threshold: parseFloat(warning)
      });
      toast.success('Пороги успешно обновлены');
      onSuccess();
    } catch (error) {
      toast.error('Ошибка обновления: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-medium mb-4">Настроить пороги: {client.Company_Name || client.Email}</h3>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="critical" className="block text-sm font-medium text-gray-700 mb-1">
              Критический уровень
            </label>
            <input
              id="critical"
              type="number"
              step="0.01"
              value={critical}
              onChange={(e) => setCritical(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              required
            />
            <p className="mt-1 text-xs text-gray-500">При достижении этого уровня будет отправлено критическое уведомление</p>
          </div>

          <div className="mb-4">
            <label htmlFor="warning" className="block text-sm font-medium text-gray-700 mb-1">
              Уровень предупреждения
            </label>
            <input
              id="warning"
              type="number"
              step="0.01"
              value={warning}
              onChange={(e) => setWarning(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              required
            />
            <p className="mt-1 text-xs text-gray-500">При достижении этого уровня будет отправлено предупреждение</p>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Markup Modal Component
const MarkupModal = ({ client, onClose, onSuccess }) => {
  const [markup, setMarkup] = useState(parseFloat(client.Markup_Percent || 0));
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (markup < 0) {
      toast.error('Наценка не может быть отрицательной');
      return;
    }
    setLoading(true);
    try {
      await api.put('/admin/billing/markup', {
        User_ID: client.User_ID,
        Markup_Percent: parseFloat(markup)
      });
      toast.success(`Наценка обновлена: ${parseFloat(markup).toFixed(1)}%`);
      onSuccess();
    } catch (error) {
      toast.error('Ошибка обновления наценки: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const effectiveRate = client._exchangeRate
    ? (parseFloat(client._exchangeRate) * (1 + markup / 100)).toFixed(4)
    : null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-medium mb-1">Наценка: {client.Company_Name || client.Email}</h3>
        <p className="text-sm text-gray-500 mb-4">
          Процент поверх себестоимости (USD × курс ЦБ). Итог: клиент платит в рублях с наценкой.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="markup" className="block text-sm font-medium text-gray-700 mb-1">
              Наценка (%)
            </label>
            <div className="relative">
              <input
                id="markup"
                type="number"
                step="0.1"
                min="0"
                max="10000"
                value={markup}
                onChange={(e) => setMarkup(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 pr-8"
                required
              />
              <span className="absolute right-3 top-2 text-gray-400 text-sm">%</span>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              0% = без наценки (себестоимость). Например, 30% означает клиент заплатит на 30% больше реальной стоимости AI.
            </p>
          </div>

          {markup > 0 && (
            <div className="mb-4 p-3 bg-indigo-50 rounded-md space-y-1">
              <p className="text-sm text-indigo-800">
                <strong>Мультипликатор:</strong> ×{(1 + parseFloat(markup) / 100).toFixed(4)}
              </p>
              <p className="text-xs text-indigo-600">
                Пример: если себестоимость $1 при курсе 90₽ → клиент заплатит{' '}
                <strong>{(90 * (1 + parseFloat(markup) / 100)).toFixed(2)} ₽</strong>
              </p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BillingAdminPanel;
